import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useGetUnreadMessageCountQuery } from 'services/chatService';
import { getTimeDiffInMs } from 'utils';
import { skipToken } from '@reduxjs/toolkit/query';
import { MessageData } from '../types';
import { selectUnreadMessagesCount } from '../chatSlice';

export const useUnreadMessagesCount = (userId: number | undefined, messageData?: MessageData) => {
  const unreadMessagesCountFromState = useSelector(selectUnreadMessagesCount);

  const lastViewedAt = useMemo(
    () =>
      messageData?.lastViewedAt.find((lastViewedData) => lastViewedData.userType === 'PROGRAM_USER')
        ?.lastViewedAt,
    [messageData],
  );

  const lastMessageDate = useMemo(
    () => messageData?.messages[messageData?.messages.length - 1]?.updatedAt,
    [messageData?.messages],
  );

  const lastViewTimeLapse = useMemo(() => {
    if (!lastViewedAt || !lastMessageDate) return null;
    return getTimeDiffInMs(lastViewedAt, lastMessageDate);
  }, [lastViewedAt, lastMessageDate]);

  // ❗skipToken tells RTK Query to fully skip the query without any internal processing
  // Adding lastViewTimeLapse to retrigger fetch when the last viewed time changes
  const unreadMessageQueryArgs = useMemo(() => {
    if (!userId) return skipToken;
    return { userId, lastViewTimeLapse: lastViewTimeLapse ?? 0 };
  }, [userId, lastViewTimeLapse]);

  useGetUnreadMessageCountQuery(unreadMessageQueryArgs, {
    refetchOnMountOrArgChange: true,
  });

  const hasUnreadMessages = useMemo(
    () => unreadMessagesCountFromState !== undefined && unreadMessagesCountFromState > 0,
    [unreadMessagesCountFromState],
  );

  return { hasUnreadMessages, unreadMessagesCount: unreadMessagesCountFromState };
};
